import { Action } from 'redux';
import RcInstanceDTO from '@robocorp/robocloud-internal-cds-sdk/lib/sdk/Types';
import { RuntimeType } from '@robocorp/robocloud-internal-cds-sdk/lib/src/types/RcInstanceDB';
import { ApiResponse } from 'apisauce';
import { ThunkAction } from 'redux-thunk';
import { activityService } from '../services/processAPI';
import { Editing, ErrorAction, OkAction } from './baseActions';
import { formatApiErrorPayload } from './utils';
import { selectCurrentWorkspaceId } from './utils/selectors';

// To avoid circular imports between modules, we must import only the AppState type here
type AppState = import('~/store').AppState;

export enum ActivityActionType {
  SILENT_GET_ACTIVITIES = 'SILENT_GET_ACTIVITIES',
  GET_ACTIVITIES = 'GET_ACTIVITIES',
  GET_ACTIVITIES_OK = 'GET_ACTIVITIES_OK',
  GET_ACTIVITIES_ERROR = 'GET_ACTIVITIES_ERROR',
  SILENT_GET_ACTIVITY = 'SILENT_GET_ACTIVITY',
  GET_ACTIVITY = 'GET_ACTIVITY',
  GET_ACTIVITY_OK = 'GET_ACTIVITY_OK',
  GET_ACTIVITY_ERROR = 'GET_ACTIVITY_ERROR',
  EDIT_ACTIVITY = 'EDIT_ACTIVITY',
  EDIT_ACTIVITY_OK = 'EDIT_ACTIVITY_OK',
  EDIT_ACTIVITY_ERROR = 'EDIT_ACTIVITY_ERROR',
  DELETE_ACTIVITY = 'DELETE_ACTIVITY',
  DELETE_ACTIVITY_OK = 'DELETE_ACTIVITY_OK',
  DELETE_ACTIVITY_ERROR = 'DELETE_ACTIVITY_ERROR',
  CREATE_ACTIVITY = 'CREATE_ACTIVITY',
  CREATE_ACTIVITY_OK = 'CREATE_ACTIVITY_OK',
  CREATE_ACTIVITY_ERROR = 'CREATE_ACTIVITY_ERROR',
  GET_WORKSPACE_ACTIVITIES = 'GET_WORKSPACE_ACTIVITIES',
  GET_WORKSPACE_ACTIVITIES_OK = 'GET_WORKSPACE_ACTIVITIES_OK',
  GET_WORKSPACE_ACTIVITIES_ERROR = 'GET_WORKSPACE_ACTIVITIES_ERROR',
}

export type ActivityEditingDetails =
  | Editing<'changingName', 'changingNameError'>
  | Editing<'changingTimeout', 'changingTimeoutError'>
  | Editing<'changingRuntime', 'changingRuntimeError'>
  | Editing<'changingCmdLine', 'changingCmdLineError'>
  | Editing<'changingPriority', 'changingPriorityError'>
  | Editing<'attachingRuntime', 'editRuntimeError'>
  | Editing<'detachingRuntime', 'editRuntimeError'>
  | Editing<'changingImage', 'changeImageError'>
  | Editing<'changingConcurrencyLimit', 'changeConcurrencyLimitError'>
  | Editing<'changingBundleUrl', 'changingBundleUrlError'>
  | Editing<'changingEnvironmentVariables', 'changingEnvironmentVariablesError'>
  | Editing<'changingDesktopNeeded', 'changingDesktopNeededError'>
  | Editing<'changingDesktopLoginTimeout', 'changingDesktopLoginTimeoutError'>
  | Editing<'changingDesktopNoSignoutAfterRun', 'changingDesktopNoSignoutAfterRunError'>
  | Editing<'changingSavingsPerWorkItem', 'changingSavingsPerWorkItemError'>
  | Editing<'changingVideoStreamingEnabled', 'changingVideoStreamingEnabledError'>;

export interface ActivityCreatePayload {
  orderNumber?: number;
  /** name of the activity, i.e. one of the activities in package.yaml */
  name?: string;
  containerImageId?: string;
  runtimeId?: string;
  runtimeType?: RuntimeType;
  environmentGroupId?: string;
  onDemandRuntimeId?: string;
}

type WithProcessId = { processId: string };
type WithActivityId = { activityId: string };

type GetActivitiesAction =
  | Action<ActivityActionType.SILENT_GET_ACTIVITIES>
  | Action<ActivityActionType.GET_ACTIVITIES>
  | OkAction<ActivityActionType.GET_ACTIVITIES_OK, RcInstanceDTO.Activity[]>
  | ErrorAction<ActivityActionType.GET_ACTIVITIES_ERROR>;

type GetActivityAction =
  | Action<ActivityActionType.SILENT_GET_ACTIVITY>
  | (Action<ActivityActionType.GET_ACTIVITY> & WithActivityId & WithProcessId)
  | (OkAction<ActivityActionType.GET_ACTIVITY_OK, RcInstanceDTO.Activity> &
      WithActivityId &
      WithProcessId)
  | (ErrorAction<ActivityActionType.GET_ACTIVITY_ERROR> & WithActivityId & WithProcessId);

type EditActivityAction =
  | (Action<ActivityActionType.EDIT_ACTIVITY> &
      WithActivityId &
      WithProcessId &
      ActivityEditingDetails)
  | (OkAction<ActivityActionType.EDIT_ACTIVITY_OK, RcInstanceDTO.Activity> &
      WithActivityId &
      WithProcessId &
      ActivityEditingDetails)
  | (ErrorAction<ActivityActionType.EDIT_ACTIVITY_ERROR> &
      WithActivityId &
      WithProcessId &
      ActivityEditingDetails);

type DeleteActivityAction =
  | (Action<ActivityActionType.DELETE_ACTIVITY> & WithActivityId)
  | (OkAction<ActivityActionType.DELETE_ACTIVITY_OK, RcInstanceDTO.Activity[]> & WithActivityId)
  | (ErrorAction<ActivityActionType.DELETE_ACTIVITY_ERROR> & WithActivityId);

type CreateActivityAction =
  | Action<ActivityActionType.CREATE_ACTIVITY>
  | OkAction<ActivityActionType.CREATE_ACTIVITY_OK, RcInstanceDTO.Activity>
  | ErrorAction<ActivityActionType.CREATE_ACTIVITY_ERROR>;

type GetWorkspaceActivitiesAction =
  | Action<ActivityActionType.GET_WORKSPACE_ACTIVITIES>
  | OkAction<ActivityActionType.GET_WORKSPACE_ACTIVITIES_OK, RcInstanceDTO.Activity[]>
  | ErrorAction<ActivityActionType.GET_WORKSPACE_ACTIVITIES_ERROR>;

export type ActivityAction =
  | GetActivitiesAction
  | GetActivityAction
  | DeleteActivityAction
  | CreateActivityAction
  | EditActivityAction
  | GetWorkspaceActivitiesAction;

export const getActivitiesByProcessId =
  (processId: string): ThunkAction<void, AppState, null, GetActivitiesAction> =>
  async (dispatch, getState) => {
    const activities = Object.values(getState().workspaceResources.activities.byId);

    if (activities.length && activities.every((activity) => activity.process_id === processId)) {
      dispatch({ type: ActivityActionType.SILENT_GET_ACTIVITIES });
    } else {
      dispatch({ type: ActivityActionType.GET_ACTIVITIES });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityService.getActivitiesByProcessId({ workspaceId, processId });
    if (response.ok) {
      dispatch({
        type: ActivityActionType.GET_ACTIVITIES_OK,
        data: response.data as RcInstanceDTO.Activity[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityActionType.GET_ACTIVITIES_ERROR,
      });
    }
  };

export const createActivity =
  (
    processId: string,
    templateId: string,
    init: ActivityCreatePayload = {},
  ): ThunkAction<void, AppState, null, CreateActivityAction> =>
  async (dispatch, getState) => {
    dispatch({ type: ActivityActionType.CREATE_ACTIVITY });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityService.create({
      workspaceId,
      processId,
      payload: { processId, templateId, ...init },
    });

    if (response.ok) {
      dispatch({
        type: ActivityActionType.CREATE_ACTIVITY_OK,
        data: response.data as RcInstanceDTO.Activity,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityActionType.CREATE_ACTIVITY_ERROR,
      });
    }
  };

export const getActivity =
  (processId: string, activityId: string): ThunkAction<void, AppState, null, GetActivityAction> =>
  async (dispatch, getState) => {
    if (getState().workspaceResources.activities.byId[activityId]) {
      dispatch({ type: ActivityActionType.SILENT_GET_ACTIVITY });
    } else {
      dispatch({
        type: ActivityActionType.GET_ACTIVITY,
        processId,
        activityId,
      });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityService.getActivity({ workspaceId, processId, activityId });
    if (response.ok) {
      dispatch({
        type: ActivityActionType.GET_ACTIVITY_OK,
        processId,
        activityId,
        data: response.data as RcInstanceDTO.Activity,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityActionType.GET_ACTIVITY_ERROR,
        processId,
        activityId,
      });
    }
  };

export const deleteActivity =
  (
    processId: string,
    activityId: string,
  ): ThunkAction<
    Promise<ApiResponse<RcInstanceDTO.Activity[]>>,
    AppState,
    null,
    DeleteActivityAction
  > =>
  async (dispatch, getState) => {
    dispatch({ type: ActivityActionType.DELETE_ACTIVITY, activityId });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await activityService.deleteActivity({ workspaceId, processId, activityId });
    if (response.ok && response.data) {
      dispatch({
        activityId,
        type: ActivityActionType.DELETE_ACTIVITY_OK,
        data: response.data,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ActivityActionType.DELETE_ACTIVITY_ERROR,
        activityId,
      });
    }

    return response;
  };
